import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useFilters } from "@/hooks/useFilters";
import { fmtDate, num } from "@/lib/format";
import { PageHeader } from "@/components/common/Page";
import { EmptyState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { BedDouble, Lock, CalendarClock, Layers, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Fact, AccommodationBrief } from "@/components/innkvartering/AccommodationBits";
import { CampTable, CampCards, UpcomingList } from "@/components/innkvartering/CampList";
import { OccupancyReport } from "@/components/innkvartering/OccupancyReport";

const VIEWS = [["oversikt", "Oversikt"], ["rapport", "Beleggsrapport"]];

/**
 * Drift → Innkvartering: portfolio across all camps the user may see (server-side scoped).
 * Oversikt: lens row · camp list · right rail with «Neste 7 dager» and the deterministic brief (design_guidelines → innkvartering_portfolio).
 * Beleggsrapport: sengedøgn per periode × camp / kunde – fakturagrunnlag.
 */
export default function AccommodationPage() {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const wide = useMediaQuery("(min-width: 1024px)");
  const [upcomingOpen, setUpcomingOpen] = useState(false);
  const { filters, setFilter } = useFilters("innkvartering", { view: "oversikt" });
  const view = VIEWS.some(([k]) => k === filters.view) ? filters.view : "oversikt";
  const allowed = can("innkvartering:read");
  // user id in the key: a scoped operator must never see another identity's cached portfolio
  const q = useQuery({ queryKey: ["ink", "portfolio", user?.id], queryFn: () => get("/innkvartering/portfolio"), enabled: allowed, refetchInterval: 120_000 });

  if (!allowed) {
    return (
      <div className="page-pad fade-up" data-testid="accommodation-page">
        <EmptyState icon={Lock} title="Ingen tilgang til innkvartering" description="Rollen din har ikke lesetilgang til belegg og beredskap. Kontakt administrator hvis du trenger campoversikten." testId="accommodation-no-access" className="py-20" />
      </div>
    );
  }

  const d = q.data;
  const t = d?.totals;
  const occTone = t?.occupancy_pct >= 95 ? "danger" : t?.occupancy_pct >= 85 ? "warning" : undefined;
  const upcomingCount = (d?.upcoming || []).reduce((a, x) => a + x.arrivals + x.departures, 0);

  return (
    <div className="page-pad space-y-6 fade-up" data-testid="accommodation-page">
      <PageHeader
        eyebrow="Drift"
        title="Innkvartering"
        count={t?.camps ? `${t.camps} camper` : undefined}
        subtitle={d ? `Belegg, ankomster og beredskap på tvers av campene · ${fmtDate(d.date, "EEEE d. MMMM")}` : "Belegg, ankomster og beredskap på tvers av campene"}
        testId="accommodation-page-header"
        actions={
          <>
            <Button variant="ghost" size="sm" className="h-8" onClick={() => q.refetch()} disabled={q.isFetching} aria-label="Oppdater" data-testid="accommodation-refresh-button"><RefreshCw className={cn("h-3.5 w-3.5", q.isFetching && "animate-spin")} /> Oppdater</Button>
            {!wide && <Button variant="outline" size="sm" className="h-8" onClick={() => setUpcomingOpen(true)} data-testid="accommodation-upcoming-open-button"><CalendarClock className="h-3.5 w-3.5" /> Neste 7 dager{upcomingCount ? ` (${upcomingCount})` : ""}</Button>}
            {can("planning:read") && <Button variant="outline" size="sm" className="h-8" onClick={() => navigate("/planlegging/kapasitet")} data-testid="accommodation-open-capacity-button"><Layers className="h-3.5 w-3.5" /> Kapasitet</Button>}
          </>
        }
      />

      <Tabs value={view} onValueChange={(v) => setFilter("view", v)}>
        <TabsList data-testid="accommodation-portfolio-tabs">
          {VIEWS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`accommodation-portfolio-view-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {view === "rapport" ? (
        <OccupancyReport />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} className="py-12" />
      ) : !d ? (
        <div className="space-y-6" data-testid="accommodation-loading">
          <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-4 lg:grid-cols-8">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
          <div className="grid gap-6 lg:grid-cols-12"><Skeleton className="h-72 lg:col-span-8" /><Skeleton className="h-72 lg:col-span-4" /></div>
        </div>
      ) : !t.camps ? (
        <EmptyState icon={BedDouble} title="Ingen aktive camper" description="Romkartet bygges automatisk når boligmoduler plasseres på et prosjekt. Reserver moduler under prosjektet, så dukker campen opp her." testId="accommodation-empty" className="py-16" action={can("projects:read") && <Button variant="outline" onClick={() => navigate("/prosjekter")}>Gå til prosjekter</Button>} />
      ) : (
        <>
          {/* lens row */}
          <div className="grid grid-cols-2 gap-x-5 gap-y-5 sm:grid-cols-4 lg:grid-cols-8" data-testid="accommodation-lens-row">
            <Fact label="Aktive camper" value={num(t.camps)} sub={t.camps_tracked ? `${t.camps_tracked} driftes av Stord` : "kunde drifter alle"} testId="accommodation-kpi-camps" />
            <Fact label="Senger totalt" value={num(t.beds_total)} sub={`${num(t.beds_available)} tilgjengelig`} testId="accommodation-kpi-beds" />
            <Fact label="Innsjekket" value={num(t.on_site)} sub={t.beds_tracked ? `av ${num(t.beds_tracked)} senger` : "ingen persondata"} testId="accommodation-kpi-on-site" />
            <Fact label="Belegg" value={t.beds_tracked ? `${t.occupancy_pct} %` : "–"} sub="campene Stord drifter" tone={occTone} testId="accommodation-kpi-occupancy" />
            <Fact label="Ankomster 7 d" value={num(t.arrivals_7d)} sub="planlagte" testId="accommodation-kpi-arrivals" onClick={!wide ? () => setUpcomingOpen(true) : undefined} />
            <Fact label="Avreiser 7 d" value={num(t.departures_7d)} sub="planlagte" testId="accommodation-kpi-departures" onClick={!wide ? () => setUpcomingOpen(true) : undefined} />
            <Fact label="Ute av drift" value={num(t.rooms_out_of_service)} sub="rom" tone={t.rooms_out_of_service ? "danger" : undefined} testId="accommodation-kpi-out-of-service" />
            <Fact label="Må rengjøres" value={num(t.rooms_to_clean)} sub="rom" tone={t.rooms_to_clean ? "warning" : undefined} testId="accommodation-kpi-to-clean" />
          </div>

          <div className="grid gap-8 lg:grid-cols-12">
            <section className="min-w-0 lg:col-span-8" data-testid="accommodation-camps">
              <header className="flex items-baseline justify-between gap-3">
                <h2 className="text-[13.5px] font-semibold leading-5 tracking-tight">Camper</h2>
                <p className="text-[12px] text-muted-foreground">{t.alerts ? `${t.alerts} varsler` : "Ingen varsler"} · sortert etter varsler og størrelse</p>
              </header>
              <div className="mt-3">{wide ? <CampTable camps={d.camps} /> : <CampCards camps={d.camps} />}</div>
            </section>

            <aside className="space-y-8 lg:col-span-4 lg:border-l lg:border-border/70 lg:pl-8" data-testid="accommodation-rail">
              <section>
                <h2 className="text-[13.5px] font-semibold leading-5 tracking-tight">Kapasitetsbrief</h2>
                <AccommodationBrief sentences={d.brief} compact className="mt-3" testId="accommodation-capacity-brief" meta="Beregnet deterministisk fra rom, opphold og crew-rotasjoner på alle camper du har tilgang til" />
                {t.release_potential_beds >= 20 && can("planning:read") && (
                  <Button variant="link" className="mt-1 h-auto px-0 text-[12.5px]" onClick={() => navigate("/planlegging/kapasitet")} data-testid="accommodation-release-to-capacity-link">Se frigjøringspotensialet i kapasitetsmotoren</Button>
                )}
              </section>
              {wide && (
                <section data-testid="accommodation-upcoming-rail">
                  <header className="flex items-baseline justify-between">
                    <h2 className="text-[13.5px] font-semibold leading-5 tracking-tight">Neste 7 dager</h2>
                    <span className="font-mono text-[12px] tabular text-muted-foreground">{t.arrivals_7d} inn · {t.departures_7d} ut</span>
                  </header>
                  <UpcomingList days={d.upcoming} className="mt-3" />
                </section>
              )}
            </aside>
          </div>
        </>
      )}

      <Sheet open={upcomingOpen} onOpenChange={setUpcomingOpen}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-xl" data-testid="accommodation-upcoming-sheet">
          <SheetHeader className="text-left">
            <SheetTitle>Neste 7 dager</SheetTitle>
            <SheetDescription>{t ? `${t.arrivals_7d} ankomster · ${t.departures_7d} avreiser. Trykk på en gruppe for å åpne dagen på campen.` : ""}</SheetDescription>
          </SheetHeader>
          <UpcomingList days={d?.upcoming || []} className="mt-4 pb-4" testId="accommodation-upcoming-sheet-list" />
        </SheetContent>
      </Sheet>
    </div>
  );
}
